import { useEffect, useRef, type ReactNode } from 'react';
import styles from './crm.module.css';

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}

/**
 * Right-docked overlay panel that floats OVER the grid (position:absolute inside the grid body) — so
 * opening/closing it never shifts the grid layout (Affinity parity, worklog E1.4/E4.1). ✕ or Escape
 * closes. Escape is ignored while an AG-Grid cell editor is open, so it cancels the edit rather than
 * closing the panel. On open, focus moves to the first focusable control (or the panel itself); on
 * close, focus returns to whatever was focused before (so keyboard users don't lose their place).
 */
export function CrmSidePanel({ title, onClose, children, width = 380 }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const ae = document.activeElement;
      if (ae && ae.closest('.ag-cell-inline-editing, .ag-popup-editor')) return; // let the grid keep Escape
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // focus into the panel on open; restore to the previously-focused element on close
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const target = panel?.querySelector<HTMLElement>('input, button, select, textarea') ?? panel;
    target?.focus();
    return () => prev?.focus?.();
  }, []);

  return (
    <div ref={panelRef} className={styles.sidePanel} style={{ width }} role="dialog" aria-modal="false" aria-label={title} tabIndex={-1}>
      <div className={styles.sidePanelHead}>
        <span className={styles.sidePanelTitle}>{title}</span>
        <button className={styles.sidePanelClose} onClick={onClose} aria-label="Close panel">✕</button>
      </div>
      <div className={styles.sidePanelBody}>{children}</div>
    </div>
  );
}
